import type { ReactNode } from "react";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

/**
 * Sidebar collapsed state — persisted to localStorage.
 * Managed in src/store/sidebar.ts; re-exported here for convenience.
 */
export { sidebarCollapsedAtom } from "./sidebar";

interface PageHeaderState {
  title: string;
  breadcrumbs?: Array<{ label: string; to?: string }>;
}

/**
 * Current page header state (title + breadcrumbs).
 * Each page sets this via the usePageHeader hook.
 */
export const pageHeaderAtom = atom<PageHeaderState>({ title: "" });

/**
 * Notification count placeholder atom.
 */
export const notificationCountAtom = atomWithStorage<number>(
  "notification_count",
  3,
);

/**
 * Users page view mode — persisted to localStorage.
 * 'cards' shows a card grid; 'list' shows a table.
 */
export const usersViewModeAtom = atomWithStorage<"cards" | "list">(
  "users_view_mode",
  "cards",
);

/**
 * Whether the global admin command palette (Cmd/Ctrl+K) is open (Sprint 142,
 * TASK-4221). Session-only (not persisted) — the palette should never re-open on
 * reload. Toggled by the global keyboard listener in `CommandPaletteProvider`
 * and by the header `CommandSearchInput` trigger.
 */
export const commandPaletteOpenAtom = atom<boolean>(false);

/**
 * Whether the route-template editor shows its map column — persisted, because
 * it is a WORKSPACE preference, not a per-template one.
 *
 * A dispatcher who works with the map open expects it open on the next
 * template and after the next reload; one who turned it off to get the full
 * stop table back would find it re-imposed on every selection otherwise. That
 * is why it lives here as one global atom rather than in the editor's own
 * state keyed by template id.
 *
 * Defaults to OFF. The small-screen, map-less layout is the primary target
 * («на мелком экране моем все разъезжается»), so the page must be usable
 * before anyone opts into the wider one.
 */
export const templateMapVisibleAtom = atomWithStorage<boolean>(
  "route_template_map_visible",
  false,
);

/**
 * The «Шаблоны» tab's trailing caption — «3 шаблона · используются 1
 * расписанием» — rendered by `RoutesSectionPage` ON THE TAB ROW (owner,
 * 2026-09-09: «должно быть в ровень с табами»).
 *
 * It lives in an atom because the two halves sit in different components and
 * neither can own both: the count is derived from the templates + schedules
 * queries inside `RouteTemplatesTab`, but the row it belongs on is the shell's
 * tab strip, one level up. Passing it up through props would mean the shell
 * holding per-tab knowledge and duplicating both queries — the exact coupling
 * `RoutesSectionPage` is documented to avoid.
 *
 * `null` = render nothing, which is also what every other tab leaves it as.
 * `RouteTemplatesTab` clears it on unmount so the caption cannot outlive the
 * tab that owns it.
 */
export const routeTemplatesCaptionAtom = atom<string | null>(null);

/**
 * Whether «Расписания» shows the list or the month calendar — persisted, the
 * same house pattern as `templateMapVisibleAtom`.
 *
 * A workspace preference, not a per-visit one: a dispatcher who plans by month
 * expects the calendar again tomorrow, and one who works from the table would
 * otherwise have it replaced on every reload. Defaults to `"list"`, because the
 * table is the cheaper view — it costs one request, while the calendar fans a
 * preview request out per schedule.
 */
export const scheduleViewModeAtom = atomWithStorage<"list" | "calendar">(
  "route_schedule_view_mode",
  "list",
);

/**
 * The «Расписания» tab's trailing caption — «1 расписание · активно» in the
 * list view, «42 рейса за месяц · 3 дня выпадают» in the calendar.
 *
 * Exists for the same reason `routeTemplatesCaptionAtom` does: the counts are
 * derived inside the tab, but the row the caption belongs on is the shell's tab
 * strip one level up. Cleared on unmount so it cannot outlive its tab.
 */
export const routeSchedulesCaptionAtom = atom<string | null>(null);

/**
 * The «Расписания» tab's toolbar — search, the view toggle and «Новое
 * расписание» — rendered by the SHELL, on the same line as the tab pills.
 *
 * The mockup puts all of it on one row. The tab strip lives in
 * `RoutesSectionPage` and the controls are owned by the tab that knows what
 * they filter, so a tab rendering them itself can only ever produce a SECOND
 * row beneath the tabs — which is what it did, and what the owner flagged
 * against the mockup twice.
 *
 * Hoisting the node rather than the state keeps the ownership right: the tab
 * still holds `search`, still owns the mutations, and the shell stays the thin
 * layout component `RoutesSectionPage` is documented to be — it renders
 * whatever node it is handed and knows nothing about schedules.
 *
 * `null` = the tab has no toolbar, which is what every other tab leaves it as.
 * Cleared on unmount so it cannot outlive the tab that owns it.
 */
export const routeSchedulesToolbarAtom = atom<ReactNode | null>(null);
